/**
 * Fotos semanales de la Hiring Plan (Agente TA Metrics V1.3, doc v5 §14): el
 * histórico de estados. El cron de los lunes toma una foto sola; acá se ven las
 * que hay y el admin de la sección puede tomar una a mano (por ejemplo la
 * primera, para que el histórico arranque hoy y no el lunes que viene).
 */
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Camera, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useTaSnapshots, useTakeTaSnapshot } from '@/hooks/useTalentAcquisition';
import { formatDateTime } from '@/lib/utils';
import { hoyBuenosAires } from '@/../../shared/ta-metrics';

export function TaSnapshotsCard() {
  const { t } = useTranslation();
  const k = 'talent_acquisition.snapshots';
  const fotos = useTaSnapshots();
  const take = useTakeTaSnapshot();
  // Misma fecha que usa la foto (Buenos Aires), no la UTC del navegador
  const hoy = hoyBuenosAires();
  const yaHoy = !!fotos.data?.some((f) => f.fecha === hoy);

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-lg">{t(`${k}.title`)}</CardTitle>
          <CardDescription>{t(`${k}.desc`)}</CardDescription>
        </div>
        <Button size="sm" className="shrink-0 gap-1.5" disabled={take.isPending || fotos.isLoading} onClick={() => take.mutate()} title={yaHoy ? t(`${k}.take_again`) : t(`${k}.take`)}>
          <Camera className="h-3.5 w-3.5" /> {take.isPending ? t(`${k}.taking`) : yaHoy ? t(`${k}.take_again`) : t(`${k}.take`)}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {fotos.isLoading && <div className="flex justify-center py-4"><WinclapLoader /></div>}
        {fotos.isError && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="flex items-center gap-1.5 text-sm text-destructive"><AlertTriangle className="h-4 w-4" />{t(`${k}.load_error`)}</p>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => fotos.refetch()}>
              <RefreshCw className="h-3.5 w-3.5" /> {t('talent_acquisition.rules.retry')}
            </Button>
          </div>
        )}
        {fotos.data && fotos.data.length === 0 && (
          <p className="text-sm text-muted-foreground">{t(`${k}.empty`)}</p>
        )}
        {fotos.data && fotos.data.length > 0 && (
          <ul className="divide-y rounded-md border text-sm">
            {fotos.data.slice(0, 12).map((f) => (
              <li key={f.fecha} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                <span className="font-medium tabular-nums">{f.fecha}</span>
                <span className="text-muted-foreground">{t(`${k}.row`, { busquedas: f.busquedas, candidatos: f.candidatos })}</span>
                <Badge variant={f.source === 'cron' ? 'secondary' : 'outline'} className="h-5 px-1.5 text-[10px]">
                  {f.source === 'cron' ? t(`${k}.source_cron`) : t(`${k}.source_manual`, { by: f.by })}
                </Badge>
                <span className="ml-auto text-xs text-muted-foreground">{f.at ? formatDateTime(f.at) : ''}</span>
              </li>
            ))}
          </ul>
        )}
        {fotos.data && fotos.data.length > 12 && (
          <p className="text-xs text-muted-foreground">{t(`${k}.more`, { n: fotos.data.length - 12 })}</p>
        )}
      </CardContent>
    </Card>
  );
}
